import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import {
  Heading, Text, UnorderedList, OrderedList, ListItem, Link, Code, Divider,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Box,
} from '@chakra-ui/react';
import { headingId } from '../lib/slug';

// 인용문(>)은 콜아웃으로 렌더링한다. 첫 줄이 "주의:" 로 시작하면 강조 색을 바꾼다.
function Callout({ children }) {
  return (
    <Box
      as="aside" bg="white" borderWidth="1px" borderColor="neutral.200" borderLeftWidth="3px"
      borderLeftColor="brand.500" borderRadius="8px" boxShadow="sm" px={5} py={4} my={5}
      sx={{ '& p': { mb: 0, fontSize: '14px' }, '& p + p': { mt: 2 } }}
    >
      {children}
    </Box>
  );
}

const components = {
  h1: (p) => <Heading as="h1" fontSize={{ base: '26px', md: '32px' }} fontWeight="700" letterSpacing="-0.032em" lineHeight="1.25" mb={3} {...p} />,
  h2: ({ children, ...p }) => (
    <Heading as="h2" id={headingId(children)} scrollMarginTop="84px" fontSize="19px" fontWeight="600"
      letterSpacing="-0.024em" mt={10} mb={3} {...p}>{children}</Heading>
  ),
  h3: (p) => <Heading as="h3" fontSize="15.5px" fontWeight="600" letterSpacing="-0.02em" mt={7} mb={2} {...p} />,
  // 문서 맨 위 `최종 수정: YYYY-MM-DD` 한 줄은 본문이 아니라 메타 정보다(원고 규칙 —
  // 모든 문서가 제목 바로 아래에 이 줄을 갖는다). 본문 톤으로 렌더하면 첫 문장처럼 읽혀서
  // 작게·흐리게 따로 그린다. 날짜는 **내용이 바뀐 날**이고, 표기 추가 같은 메타 변경으로는
  // 올리지 않는다.
  p: (p) => {
    const first = Array.isArray(p.children) ? p.children[0] : p.children;
    if (typeof first === 'string' && /^최종 수정: \d{4}-\d{2}-\d{2}$/.test(first.trim())) {
      return <Text fontSize="12.5px" color="neutral.400" letterSpacing="0.01em" mt={-1} mb={7} {...p} />;
    }
    return <Text fontSize="15px" color="neutral.600" lineHeight="1.75" mb={4} {...p} />;
  },
  ul: (p) => <UnorderedList spacing={2} mb={4} ml={5} {...p} />,
  ol: (p) => <OrderedList spacing={2} mb={4} ml={5} {...p} />,
  li: (p) => <ListItem fontSize="15px" color="neutral.600" lineHeight="1.75" {...p} />,
  // 원고끼리의 링크는 `c-works.md` 형태로 쓴다. slug 가 곧 주소이므로 라우터 링크로 바꿔 준다.
  a: ({ href = '', ...p }) => {
    const internal = /^[\w-]+\.md(#.*)?$/.test(href);
    const to = internal ? `/${href.replace(/\.md/, '')}` : href;
    return (
      <Link
        {...(internal ? { as: RouterLink, to } : { href, isExternal: /^https?:/.test(href) })}
        color="brand.700" textDecoration="underline" textUnderlineOffset="2px" {...p}
      />
    );
  },
  strong: (p) => <Text as="strong" color="neutral.800" fontWeight="600" {...p} />,
  code: (p) => <Code fontSize="0.88em" bg="neutral.100" borderWidth="1px" borderColor="neutral.200" borderRadius="4px" px={1.5} {...p} />,
  hr: () => <Divider my={8} borderColor="neutral.200" />,
  blockquote: (p) => <Callout {...p} />,
  table: (p) => (
    <TableContainer borderWidth="1px" borderColor="neutral.200" borderRadius="8px" bg="white" boxShadow="sm" mb={5}>
      <Table size="sm" {...p} />
    </TableContainer>
  ),
  thead: (p) => <Thead bg="neutral.100" {...p} />,
  tbody: (p) => <Tbody {...p} />,
  tr: (p) => <Tr {...p} />,
  th: (p) => <Th fontSize="10.5px" letterSpacing="0.09em" color="neutral.500" borderColor="neutral.200" py={3} {...p} />,
  td: (p) => <Td fontSize="14px" color="neutral.600" borderColor="neutral.200" py={3} whiteSpace="normal" {...p} />,
};

export default function Markdown({ children }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  );
}
